import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Divider
} from '@mui/material';
import { Google, Microsoft, Login } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { AuthModalView } from './AuthModal.tsx';

interface SignInFormProps {
  onViewChange: (view: AuthModalView) => void;
  onSuccess: () => void;
}

const SignInForm: React.FC<SignInFormProps> = ({ onViewChange, onSuccess }) => {
  const { signIn, error, isLoading, clearError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formErrors, setFormErrors] = useState<{email?: string; password?: string}>({});

  const validateForm = (): boolean => {
    const errors: {email?: string; password?: string} = {};
    
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!password) {
      errors.password = 'Password is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) return;

    try {
      clearError();
      await signIn(email, password);
      onSuccess();
    } catch {
      // Error handled by context
    }
  };

const handleSocialSignIn = (provider: 'google' | 'microsoft') => {
  const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  const redirectUri = window.location.origin; // This automatically uses the current domain
  
  const providerMap = {
    google: 'Google',
    microsoft: 'AzureAD'
  };
  
  const authUrl = `${cognitoDomain}/oauth2/authorize?` +
    `identity_provider=${providerMap[provider]}&` +
    `redirect_uri=${encodeURIComponent(redirectUri)}&` +
    `response_type=code&` +
    `client_id=${clientId}&` +
    `scope=openid email profile`;
    
  window.location.href = authUrl;
};



  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 300, mx: 'auto' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

{/*       <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        Please Sign in:
      </Typography>
 */}
      <TextField
        label="Email Address"
        type="email"
        fullWidth
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (formErrors.email) setFormErrors(prev => ({ ...prev, email: undefined }));
          clearError();
        }}
        error={!!formErrors.email}
        helperText={formErrors.email}
        disabled={isLoading}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2
          }
        }}
      />

      <TextField
        label="Password"
        type="password"
        fullWidth
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
          if (formErrors.password) setFormErrors(prev => ({ ...prev, password: undefined }));
          clearError();
        }}
        error={!!formErrors.password}
        helperText={formErrors.password}
        disabled={isLoading}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2
          }
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={isLoading}
        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Login />}
        sx={{
          mb: 3,
          py: 1.5,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #303f9f 0%, #1976d2 100%)',
          }
        }}
      >
        {isLoading ? 'Signing In...' : 'Sign In'}
      </Button>

      <Divider sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          or continue with
        </Typography>
      </Divider>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
      <Button
        fullWidth
        variant="outlined"
        startIcon={<Google />}
        onClick={() => handleSocialSignIn('google')}
        disabled={isLoading}
        sx={{ borderRadius: 2 }}
      >
        Google
      </Button>
      <Button
        fullWidth
        variant="outlined"
        startIcon={<Microsoft />}
        onClick={() => handleSocialSignIn('microsoft')}
        disabled={isLoading}
        sx={{ borderRadius: 2 }}
      >
        Microsoft
      </Button>
    </Box>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Don't have an account?{' '}
          <Link
            component="button"
            type="button"
            onClick={() => onViewChange('signup')}
            sx={{ fontWeight: 600, textDecoration: 'none' }}
          >
            Sign up here
          </Link>
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Forgot your password?{' '}
          <Link
            href="#"
            onClick={() => console.log('Reset password - implement later')}
            sx={{ fontWeight: 600, textDecoration: 'none' }}
          >
            Reset it here
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default SignInForm;